import React, { Fragment, useEffect, useState } from 'react'
import { Edit } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { SnackBar } from '../../components/SnackBar/Snackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Status } from '../../hooks/useFetchState'

import { filterOptions } from './Config/filterOptions'
import { getColumnsConfig } from './Config/tableColumns'
import { getProductsOrigins } from './api'
import { ProductOriginModal } from './Modal/ProductOriginModal'

import { ProductOrigin } from '../../interfaces/Producto/ProductOrigins'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { ApiResponse } from '../../typings/ApiResponse'

export const ProductOriginPage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<ProductOrigin>>()
  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const [orderBy, setOrderBy] = useState<GenericOrderBy<ProductOrigin>>({
    field: 'name' as keyof ProductOrigin,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const editModal = useModal<ProductOrigin>('Editar Marca')

  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(
      getProductsOrigins(filtersToUse, orderBy, pageToUse, pageSizeToUse)
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const resetAndFetchData = (filters?: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  const handleCloseModalMessage = (success: boolean, message: string) => {
    openSnackBar(message, success)
    if (success) {
      editModal.closeModal()
      requestData(selectedFilters, pagination.page, pagination.pageSize)
    }
  }

  const handleSorting = (
    field: keyof ProductOrigin,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  useEffect(() => {
    resetAndFetchData(selectedFilters)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          onSearchClick={resetAndFetchData}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
        />
        <GenericTableV2<ProductOrigin>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='productOriginId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof ProductOrigin,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('xl', item),
              icon: <Edit />
            }
          ]}
        />
      </StandardPage>

      <ProductOriginModal
        isOpen={editModal.isOpen}
        size={editModal.size}
        extendedProps={editModal.extendedProps}
        closeModal={editModal.closeModal}
        handleCloseModalMessage={handleCloseModalMessage}
      />

      <SnackBar
        open={isActive}
        message={message}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
